import React from "react";
import { Box, Chip, Paper, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import type { SvgIconTypeMap } from "@mui/material/SvgIcon";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";

type SvgIconComponent = OverridableComponent<SvgIconTypeMap>;

interface DailyTasksEmptyStateProps {
  title: string;
  description?: string;
  icon?: SvgIconComponent;
  /** When true, wraps in the standard section paper. Default true. */
  inPaper?: boolean;
  sx?: object;
}

export const DailyTasksEmptyState: React.FC<DailyTasksEmptyStateProps> = ({
  title,
  description,
  icon: Icon = InboxOutlinedIcon,
  inPaper = true,
  sx,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        py: 2.5,
        px: 2,
        borderRadius: 2,
        border: `1px dashed ${pageColors.border}`,
        bgcolor: alpha(theme.palette.grey[500], 0.03),
        ...(!inPaper ? sx : undefined),
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.25,
          bgcolor: alpha(colors.purple, 0.1),
          color: colors.purple,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <CustomTypography
        variant="body2"
        fontWeight={600}
        sx={{ color: pageColors.title }}
      >
        {title}
      </CustomTypography>
      {description ? (
        <CustomTypography
          variant="body3"
          fontWeight={500}
          sx={{
            color: pageColors.subtitle,
            mt: 0.5,
            maxWidth: 320,
            lineHeight: 1.45,
          }}
        >
          {description}
        </CustomTypography>
      ) : null}
    </Box>
  );

  if (!inPaper) return content;

  return (
    <Paper elevation={0} sx={{ ...modernSectionPaperSx(theme), ...sx }}>
      {content}
    </Paper>
  );
};

interface DailyTasksSectionHeaderProps {
  title: string;
  count?: number;
  sx?: object;
}

export const DailyTasksSectionHeader: React.FC<DailyTasksSectionHeaderProps> = ({
  title,
  count,
  sx,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        mb: 1.25,
        ...sx,
      }}
    >
      <CustomTypography
        variant="heading7"
        fontWeight={600}
        sx={{ color: pageColors.title }}
      >
        {title}
      </CustomTypography>
      {count != null ? (
        <Chip
          size="small"
          label={count}
          sx={{
            height: 22,
            minWidth: 28,
            fontWeight: 600,
            fontSize: "0.75rem",
            bgcolor:
              count === 0
                ? alpha(theme.palette.grey[600], 0.1)
                : alpha(colors.purple, 0.1),
            color: count === 0 ? theme.palette.grey[700] : colors.purple,
          }}
        />
      ) : null}
    </Box>
  );
};

export default DailyTasksEmptyState;
